import { useState, useEffect, useCallback } from 'react';
import { format, addMinutes, isBefore } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { supabase } from '../lib/supabase';
import { uploadImageToStorage, deleteImageFromStorage } from '../lib/fileUpload';
import { overlayLogoAndUpload, compositeAndUpload } from '../lib/overlayLogo';
import toast from 'react-hot-toast';
import type { TaskFormProps } from '../components/task/types';

const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw';
  } catch {
    return 'Europe/Warsaw';
  }
};

export function useTaskForm({ onClose, onSubmit, editingTask }: TaskFormProps) {
  const [content, setContent] = useState(editingTask?.content || '');
  const [selectedPlatform, setSelectedPlatform] = useState(editingTask?.platform || 'facebook');
  const [selectedAccount, setSelectedAccount] = useState<string | null>(editingTask?.account_id || null);
  const [selectedDate, setSelectedDate] = useState(
    editingTask ? new Date(editingTask.scheduled_for) : new Date()
  );
  const [selectedTime, setSelectedTime] = useState(
    editingTask ? format(new Date(editingTask.scheduled_for), 'HH:mm') : format(addMinutes(new Date(), 10), 'HH:mm')
  );
  const [selectedTimezone, setSelectedTimezone] = useState(
    editingTask?.timezone || detectBrowserTimezone()
  );
  const [userDefaultTimezone, setUserDefaultTimezone] = useState<string | null>(null);
  const [cycle, setCycle] = useState(editingTask?.cycle || 'once');
  const [selectedDays, setSelectedDays] = useState<string[]>(editingTask?.cycle_days || []);
  const [customWebhook, setCustomWebhook] = useState(
    editingTask?.webhook_url === import.meta.env.VITE_DEFAULT_WEBHOOK ? '' : (editingTask?.webhook_url || '')
  );
  const [imagePreview, setImagePreview] = useState(editingTask?.image_url || '');
  const [generateImage, setGenerateImage] = useState(editingTask?.generate_image || false);
  const [modifyImage, setModifyImage] = useState(editingTask?.modify_image || false);
  const [fullModifyImage, setFullModifyImage] = useState(editingTask?.full_modify_image || false);
  const [isAiGenerated, setIsAiGenerated] = useState(
    editingTask?.metadata?.is_ai_generated !== undefined
      ? editingTask.metadata.is_ai_generated
      : true // Default to true
  );
  const [generateText, setGenerateText] = useState(
    editingTask?.metadata?.generate_text !== undefined
      ? editingTask.metadata.generate_text
      : true // Default to true (AI generates text by default)
  );
  const [showCustomContentModal, setShowCustomContentModal] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [minTime, setMinTime] = useState(getMinTime());
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(
    editingTask?.metadata?.logo_url || null
  );

  useEffect(() => {
    loadConnectedAccounts();
    loadUserDefaultTimezone();
    const interval = setInterval(() => {
      setMinTime(getMinTime());
    }, 60000);

    return () => clearInterval(interval);
  }, [selectedPlatform]);

  const loadUserDefaultTimezone = useCallback(async () => {
    if (editingTask?.timezone) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('default_timezone')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user timezone:', error);
        return;
      }

      if (data?.default_timezone) {
        setUserDefaultTimezone(data.default_timezone);
        setSelectedTimezone(data.default_timezone);
      }
    } catch (error) {
      console.error('Error loading user default timezone:', error);
    }
  }, [editingTask?.timezone]);

  function getMinTime(): string {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    const minDateTime = addMinutes(zonedNow, 10);
    return format(minDateTime, 'HH:mm');
  }

  function getCurrentTimeInTimezone(): string {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    return format(zonedNow, 'HH:mm');
  }

  const autoCorrectDateTime = (): { date: Date; time: string } => {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    const selectedDateTime = zonedTimeToUtc(
      new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}`),
      selectedTimezone
    );
    const minDateTime = addMinutes(zonedNow, 10);

    if (isBefore(selectedDateTime, minDateTime)) {
      // Auto-correct to minimum time (now + 10 minutes)
      const correctedDate = minDateTime;
      const correctedTime = format(correctedDate, 'HH:mm');

      toast.success('Time automatically adjusted to +10 minutes from now', {
        duration: 3000,
        icon: '⏰',
      });

      return { date: correctedDate, time: correctedTime };
    }

    return { date: selectedDate, time: selectedTime };
  };

  const loadConnectedAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Load from connected_accounts (required for scheduled_tasks foreign key)
      const { data, error } = await supabase
        .from('connected_accounts')
        .select('*')
        .eq('platform', selectedPlatform)
        .eq('user_id', user.id);

      if (error) throw error;

      // Transform to match expected format
      const transformedData = (data || []).map(account => ({
        id: account.id,
        name: account.name || 'Unnamed Account',
        platform: account.platform,
        account_type: account.account_type || account.platform,
        status: account.status
      }));

      // Sort alphabetically by name for easier navigation
      transformedData.sort((a, b) => a.name.localeCompare(b.name));

      console.log('Loaded connected accounts:', transformedData.length, 'accounts');
      console.log('First 5 accounts:', transformedData.slice(0, 5).map(a => a.name));

      setConnectedAccounts(transformedData);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Failed to load connected accounts');
    }
  };

  const handlePlatformSelect = (platform: string) => {
    setSelectedPlatform(platform);
    setSelectedAccount(null);
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccount(accountId);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageToCrop(file);
      setShowCropModal(true);
      e.target.value = '';
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    try {
      setIsUploading(true);
      setShowCropModal(false);
      toast.loading('Uploading cropped image...');

      // Delete previous image if exists
      if (imagePreview && !imagePreview.startsWith('data:')) {
        await deleteImageFromStorage(imagePreview);
      }

      // Upload cropped image
      const { publicUrl, error } = await uploadImageToStorage(croppedFile);

      if (error) {
        throw error;
      }

      if (!publicUrl) {
        throw new Error('Failed to get public URL for uploaded image');
      }

      setImagePreview(publicUrl);
      setGenerateImage(false);
      setImageToCrop(null);

      toast.dismiss();
      toast.success('Image cropped and uploaded successfully');
    } catch (error) {
      console.error('Error handling image:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Failed to upload image');

      setImagePreview('');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleImageRemove = async () => {
    try {
      setIsUploading(true);
      if (imagePreview && !imagePreview.startsWith('data:')) {
        await deleteImageFromStorage(imagePreview);
      }
      setImagePreview('');
      setModifyImage(false);
      toast.success('Image removed successfully');
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Failed to remove image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateImageToggle = () => {
    setGenerateImage(!generateImage);
    if (!generateImage) {
      setImagePreview('');
      setModifyImage(false);
      setFullModifyImage(false);
    }
  };

  const handleModifyImageToggle = () => {
    const newValue = !modifyImage;
    setModifyImage(newValue);
    if (newValue) {
      setFullModifyImage(false);
    }
  };

  const handleFullModifyImageToggle = () => {
    console.log('[DEBUG] handleFullModifyImageToggle called, current fullModifyImage:', fullModifyImage);
    const newValue = !fullModifyImage;
    console.log('[DEBUG] Setting fullModifyImage to:', newValue);
    setFullModifyImage(newValue);
    if (newValue) {
      console.log('[DEBUG] Disabling modifyImage');
      setModifyImage(false);
    }
  };

  const handleAiGeneratedToggle = () => {
    setIsAiGenerated(!isAiGenerated);
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
  };

  const handleTimezoneChange = (timezone: string) => {
    setSelectedTimezone(timezone);
    setSelectedTime(getCurrentTimeInTimezone());
    setMinTime(getMinTime());
  };

  const handleCycleChange = (newCycle: string) => {
    setCycle(newCycle);
  };

  const handleDaysChange = (days: string[]) => {
    setSelectedDays(days);
  };

  const handleWebhookChange = (url: string) => {
    setCustomWebhook(url);
  };

  const handleWebhookInfoToggle = () => {
    setShowWebhookInfo(!showWebhookInfo);
  };

  const handleAIAssistantToggle = () => {
    if (!showAIAssistant) {
      // Opening AI Assistant — switch back to AI text generation mode
      // This fixes the bug where after using "Własny opis" (generateText=false),
      // clicking "AI Assistant" wouldn't restore generateText to true.
      setGenerateText(true);
    }
    setShowAIAssistant(!showAIAssistant);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const { publicUrl, error } = await uploadImageToStorage(file);
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');
      setLogoUrl(publicUrl);
      toast.success('Logo dodane');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Nie udało się przesłać logo');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    try {
      setIsUploading(true);
      if (logoUrl) await deleteImageFromStorage(logoUrl);
      setLogoUrl(null);
      toast.success('Logo usunięte');
    } catch (error) {
      console.error('Error removing logo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCustomContentModalToggle = () => {
    if (!showCustomContentModal) {
      // Opening custom content modal — switch to exact text mode immediately
      // This provides instant visual feedback via textarea styling
      setGenerateText(false);
    }
    setShowCustomContentModal(!showCustomContentModal);
  };

  const handleAIContentGenerated = (generatedContent: string) => {
    setContent(generatedContent);
    // Since AI generated it, we might still want the webhook to process it OR we can set generateText to false 
    // depending on the intended workflow. If the assistant generates FINAL text, we should probably set generateText = false.
    // However, to keep it safe, if they use "Własny opis" we definitively set it to false.
    setGenerateText(false);
  };

  const handleCustomContentSave = (customContent: string) => {
    setContent(customContent);
    setGenerateText(false); // Do not rewrite this text in n8n
    setShowCustomContentModal(false);
  };

  const handleTestWebhook = async () => {
    if (!content || !selectedAccount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Instagram requires an image
    if ((selectedPlatform === 'instagram' || selectedPlatform === 'instagram_direct') && !imagePreview && !generateImage) {
      toast.error('Instagram requires an image. Please upload an image or enable "Generate image with AI"');
      return;
    }

    // Auto-correct datetime if needed (same logic as handleSubmit)
    autoCorrectDateTime();

    setIsTesting(true);
    const loadingToast = toast.loading('Testing webhook...');

    try {
      const webhookUrl = customWebhook || import.meta.env.VITE_DEFAULT_WEBHOOK;
      
      if (!webhookUrl) {
        throw new Error('No webhook URL configured');
      }

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Get task details if editing
      let taskDetails = null;
      if (editingTask) {
        const { data, error } = await supabase
          .from('scheduled_tasks')
          .select('*')
          .eq('id', editingTask.id)
          .single();
          
        if (error) throw error;
        taskDetails = data;
      }

      const needsGeneration = generateText || generateImage || modifyImage || fullModifyImage;

      if (!needsGeneration) {
        // Bypass n8n entirely
        const { data: postId, error: rpcError } = await supabase.rpc('create_direct_post', {
          p_task_id: editingTask?.id || null,
          p_user_id: user.id,
          p_content: content,
          p_image_url: imagePreview,
          p_is_test: true
        });

        if (rpcError) throw rpcError;

        toast.dismiss(loadingToast);
        toast.success('Test completed successfully! (Bypassed AI generation)');
        await onSubmit();
        onClose();
        return;
      }

      // Make direct webhook request for generation
      const webhookData = {
        text: content,
        content: content,  // Alias for text (matches SQL webhook format)
        account_id: selectedAccount,
        platform: selectedPlatform,
        generate_image: generateImage,
        use_image_as_reference: modifyImage,
        full_modify_image: fullModifyImage,
        tool_id: editingTask?.id || 'test',
        task_id: editingTask?.id || 'test',  // n8n correlates via task_id
        user_id: user.id,  // CRITICAL: n8n needs user_id for brand/CTA overlay lookup
        usage_id: `test-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        is_test: true,
        image_url: imagePreview,
        logo_url: logoUrl,  // Logo URL for overlay
        // Add the requested information with correct values
        generate: generateImage,
        modify: modifyImage,
        full_modify: fullModifyImage,
        generate_text: generateText,
        photo: !!imagePreview,
        content_type: 'post',
        test: true,
        webhookUrl: webhookUrl,
        executionMode: 'test'
      };

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify(webhookData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Webhook failed with status ${response.status}: ${errorText}`);
      }

      // Parse the response - handle both array and object formats
      const rawResult = await response.json();
      
      // Check if response is an array and extract the first item
      const result = Array.isArray(rawResult) ? rawResult[0] : rawResult;
      
      if (!result || !result.data) {
        throw new Error('Invalid webhook response format');
      }

      // STEP 1: Composite AI photo onto branded template (if task has a template image)
      // The template (imagePreview / task's image_url) contains branding elements
      let finalImageUrl = result.data.image_url || imagePreview;
      if (imagePreview && finalImageUrl && imagePreview !== finalImageUrl) {
        try {
          toast.loading('Nakładanie szablonu...');
          finalImageUrl = await compositeAndUpload(
            imagePreview,
            finalImageUrl,
            uploadImageToStorage
          );
          toast.dismiss();
        } catch (err) {
          console.error('[handleTestWebhook] Template composite failed:', err);
          toast.dismiss();
        }
      }

      // STEP 2: Apply logo overlay if we have a separate logo_url
      if (logoUrl && finalImageUrl) {
        try {
          toast.loading('Nakładanie logo...');
          finalImageUrl = await overlayLogoAndUpload(
            finalImageUrl,
            logoUrl,
            uploadImageToStorage
          );
          toast.dismiss();
        } catch (err) {
          console.error('[handleTestWebhook] Logo overlay failed:', err);
          toast.dismiss();
        }
      }


      // Create a post with the webhook response
      // CRITICAL: When generateText=false, preserve the original content
      const { data: newPost, error: insertError } = await supabase
        .from('posts')
        .insert([{
          content: generateText ? (result.data.content || content) : content,
          platform: selectedPlatform,
          published_at: new Date().toISOString(),
          image_url: finalImageUrl,
          task_id: editingTask?.id,
          user_id: user.id,
          status: 'ready',
          metadata: {
            webhook_response: result,
            is_test: true,
            stage: 'ready',
            is_in_progress: false,
            generate_text: generateText,
            content_preserved: !generateText,
            creation_timestamp: new Date().toISOString(),
            webhook_complete_timestamp: new Date().toISOString(),
            webhook_payload: webhookData
          }
        }])
        .select()
        .single();

      if (insertError) {
        throw new Error(`Failed to create test post: ${insertError.message}`);
      }

      // Now that we have a valid post record, we can safely create the webhook log
      try {
        await supabase
          .from('webhook_logs')
          .insert([{
            user_id: user.id,
            task_id: editingTask?.id,
            post_id: newPost.id,
            webhook_url: webhookUrl,
            request_payload: webhookData,
            response_payload: result,
            status: 'success',
            event_type: 'test_post'
          }]);
      } catch (logError) {
        console.error('Failed to create webhook log for test post:', logError);
        // Don't throw here as the post was created successfully
      }

      toast.dismiss(loadingToast);
      toast.success('Test completed successfully!');
      await onSubmit();
      onClose();
    } catch (error) {
      console.error('Test webhook error:', error);
      toast.dismiss(loadingToast);
      toast.error(error instanceof Error ? error.message : 'Webhook test failed. Please try again.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!content) {
      toast.error('Please enter content for your task');
      return;
    }

    if (!selectedAccount) {
      toast.error('Please select an account to post from');
      return;
    }

    // Instagram requires an image
    if ((selectedPlatform === 'instagram' || selectedPlatform === 'instagram_direct') && !imagePreview && !generateImage) {
      toast.error('Instagram requires an image. Please upload an image or enable "Generate image with AI"');
      return;
    }

    // Auto-correct time if needed (instead of blocking submission)
    const { date: correctedDate, time: correctedTime } = autoCorrectDateTime();

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const webhookUrl = customWebhook || import.meta.env.VITE_DEFAULT_WEBHOOK;

      if (!webhookUrl) {
        throw new Error('No webhook URL configured');
      }

      const scheduledDate = new Date(`${format(correctedDate, 'yyyy-MM-dd')}T${correctedTime}`);
      const scheduledInTimezone = formatInTimeZone(
        scheduledDate,
        selectedTimezone,
        "yyyy-MM-dd'T'HH:mm:ssXXX"
      );
      
      const taskData = {
        content,
        platform: selectedPlatform,
        account_id: selectedAccount,
        scheduled_for: scheduledInTimezone,
        webhook_url: webhookUrl,
        cycle,
        cycle_days: cycle === 'custom' ? selectedDays : null,
        image_url: imagePreview || null,
        generate_image: generateImage,
        modify_image: modifyImage,
        full_modify_image: fullModifyImage,
        content_type: 'post',
        metadata: {
          is_ai_generated: isAiGenerated,
          generate_text: generateText,
          logo_url: logoUrl,
        },
        status: 'active',
        user_id: user.id,
        timezone: selectedTimezone
      };

      let result;
      if (editingTask) {
        result = await supabase
          .from('scheduled_tasks')
          .update(taskData)
          .eq('id', editingTask.id)
          .eq('user_id', user.id);
      } else {
        result = await supabase
          .from('scheduled_tasks')
          .insert([taskData]);
      }

      if (result.error) throw result.error;

      toast.success(editingTask ? 'Task updated successfully!' : 'Task scheduled successfully!');
      await onSubmit();
      onClose();
    } catch (error) {
      console.error('Error scheduling task:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : typeof error === 'object' && error !== null
          ? JSON.stringify(error)
          : 'Failed to schedule task. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const returnObject = {
    content,
    setContent,
    selectedPlatform,
    selectedAccount,
    selectedDate,
    selectedTime,
    selectedTimezone,
    userDefaultTimezone,
    cycle,
    selectedDays,
    customWebhook,
    imagePreview,
    generateImage,
    modifyImage,
    fullModifyImage,
    isAiGenerated,
    connectedAccounts,
    isLoading,
    isTesting,
    isUploading,
    minTime,
    showWebhookInfo,
    imageToCrop,
    showCropModal,
    showAIAssistant,
    showCustomContentModal,
    generateText,
    setGenerateText,
    logoUrl,
    handleLogoUpload,
    handleLogoRemove,
    handlePlatformSelect,
    handleAccountSelect,
    handleImageChange,
    handleCropComplete,
    handleCropCancel,
    handleGenerateImageToggle,
    handleModifyImageToggle,
    handleFullModifyImageToggle,
    handleAiGeneratedToggle,
    handleImageRemove,
    handleDateChange,
    handleTimeChange,
    handleTimezoneChange,
    handleCycleChange,
    handleDaysChange,
    handleWebhookChange,
    handleWebhookInfoToggle,
    handleAIAssistantToggle,
    handleCustomContentModalToggle,
    handleAIContentGenerated,
    handleCustomContentSave,
    handleTestWebhook,
    handleSubmit,
  };

  console.log('[useTaskForm] Returning:', {
    fullModifyImage,
    handleFullModifyImageToggle: typeof handleFullModifyImageToggle,
  });

  return returnObject;
}